import type { StageDwellSegment } from '@/services/loans'
import { parseDateValue } from '@/utils/dates'
import { LOAN_STAGES, type LoanStage } from '@/types'
import type { DossierDisplayStageId, DossierPipelineStage, DossierStageStatus } from './types'

/** The dossier's stage strip: intake, then the canonical stages in order under
 *  the same names the Pipeline rail and report bands use. The strip drew
 *  'Underwriting' (= Term Sheet Signed) before 'Term Sheet' (= Issued), so a
 *  signed deal showed the issued step as still to come (OVR-16). */
export const DOSSIER_DISPLAY_STAGES: { id: DossierDisplayStageId; label: string; loanStage: LoanStage | null }[] = [
  { id: 'intake', label: 'Intake', loanStage: null },
  { id: 'triage', label: 'Triage', loanStage: 'Triage' },
  { id: 'prescreen', label: 'Pre-screen', loanStage: 'Pre-screen Indication' },
  { id: 'quote', label: 'Quote', loanStage: 'Post-screen Quote' },
  { id: 'termsheet', label: 'TS Issued', loanStage: 'Term Sheet Issued' },
  { id: 'underwriting', label: 'TS Signed', loanStage: 'Term Sheet Signed' },
  { id: 'committee', label: 'IC Approved', loanStage: 'IC / Committee Approved' },
  { id: 'closing', label: 'Rate Lock', loanStage: 'Rate Lock' },
  { id: 'funded', label: 'Funded', loanStage: 'Funded' },
]

/** Which display stage is active for a canonical loan stage. */
export function displayStageIndexForLoanStage(stage: string | undefined): number {
  if (!stage) return 0
  const idx = LOAN_STAGES.indexOf(stage as LoanStage)
  if (idx < 0) return 0
  // Display stages are intake + the canonical stages in canonical order.
  return idx + 1
}

function formatRange(segment: StageDwellSegment | undefined): string | null {
  if (!segment?.enteredAt) return null
  const start = parseDateValue(segment.enteredAt)
  if (!start) return null
  const startText = start.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
  if (!segment.exitedAt) return `${startText} – present`
  const end = parseDateValue(segment.exitedAt)
  if (!end) return startText
  const endText = end.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
  return `${startText} – ${endText}`
}

export function buildPipelineStages(
  currentStage: string | undefined,
  assignee: string | null,
  segments: StageDwellSegment[],
  originated: string | null,
): DossierPipelineStage[] {
  const currentDisplayIdx = displayStageIndexForLoanStage(currentStage)
  const segmentByStage = new Map(segments.map((s) => [s.stage, s]))

  return DOSSIER_DISPLAY_STAGES.map((def, index) => {
    let status: DossierStageStatus = 'upcoming'
    if (index < currentDisplayIdx) status = 'completed'
    else if (index === currentDisplayIdx) status = 'current'
    else if (def.id === 'intake' && originated) status = 'completed'

    const segment = def.loanStage ? segmentByStage.get(def.loanStage) : undefined
    // `originated` is a date-only value: the day received, in the reader's zone.
    const received = def.id === 'intake' ? parseDateValue(originated) : null
    const dateRange = received
      ? received.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
      : formatRange(segment)

    return {
      id: def.id,
      label: def.label,
      status,
      loanStage: def.loanStage,
      dateRange,
      days: segment?.days ?? null,
      // Only the stage the deal is in now is the current assignee's: who held an earlier stage is not
      // recorded here, and crediting every completed stage to today's assignee stated what nothing says.
      owner: segment?.current ? assignee : null,
      docCount: null,
      commentCount: null,
      // No count is recorded per stage. A red "1" on every deal's current stage was invented.
      alertCount: null,
    }
  })
}
